import type { ComponentType } from 'react';
import {
    AreaIcon,
    BalconyIcon,
    CalendarIcon,
    CarportIcon,
    CellarIcon,
    EnergyCertIcon,
    FloorCountIcon,
    FurnishingsIcon,
    GarageIcon,
    HouseIcon,
    LoggiaIcon,
    MonthlyExpensesIcon,
    OutdoorParkingIcon,
    RoomCountIcon,
    ShinesIcon,
    TerraceIcon,
} from '@/shared/components/icons';
import {
    ListingDetailResponseDtoApartmentConstructionType,
    ListingDetailResponseDtoHouseConstructionType,
    ListingDetailResponseDtoPropertyConditionType,
    ListingDetailResponseDtoFurnishingType,
    ListingDetailResponseDtoLandReadiness,
    ListingDetailOutdoorSpaceResponseDtoKind,
    ListingDetailParkingSlotResponseDtoParkingType,
} from '@/api/generated/model';
import type { Listing } from '../types';
import { ListingPropertyCategory } from '../definitions/propertyTypes';
import { OutdoorSpaceKind } from '../definitions';
import { ListingDetailedInfoItem, ListingMainInfoItem } from '../types';

// --- Label mappers ---

export function mapApartmentConstructionTypeToText(
    type: ListingDetailResponseDtoApartmentConstructionType,
): string {
    switch (type) {
        case 'PANEL':
            return 'Panelová';
        case 'BRICK':
            return 'Tehlová';
        case 'MIXED':
            return 'Kombinovaná';
        case 'SKELETAL':
            return 'Skeletová';
        case 'PREFABRICATED':
            return 'Montovaná';
        case 'OTHER':
            return 'Iná';
        default:
            return '';
    }
}

export function mapHouseConstructionTypeToText(
    type: ListingDetailResponseDtoHouseConstructionType,
): string {
    switch (type) {
        case 'BRICK':
            return 'Tehlová';
        case 'WOODEN':
            return 'Drevená';
        case 'PREFABRICATED':
            return 'Montovaná';
        case 'MIXED':
            return 'Kombinovaná';
        case 'BLOCK':
            return 'Bloková';
        case 'OTHER':
            return 'Iná';
        default:
            return '';
    }
}

export function mapPropertyConditionTypeToText(
    type: ListingDetailResponseDtoPropertyConditionType,
): string {
    switch (type) {
        case 'NEW_BUILD':
            return 'Novostavba';
        case 'FULL_RECONSTRUCTION':
            return 'Kompletná rekonštrukcia';
        case 'PARTIAL_RECONSTRUCTION':
            return 'Čiastočná rekonštrukcia';
        case 'ORIGINAL':
            return 'Pôvodný stav';
        case 'UNDER_CONSTRUCTION':
            return 'Vo výstavbe';
        case 'OTHER':
            return 'Iný stav';
        default:
            return '';
    }
}

export function mapFurnishingTypeToText(type: ListingDetailResponseDtoFurnishingType): string {
    switch (type) {
        case 'FULL':
            return 'Zariadený';
        case 'PARTIAL':
            return 'Čiastočne zariadený';
        case 'UNFURNISHED':
            return 'Nezariadený';
        case 'BY_AGREEMENT':
            return 'Podľa dohody';
        default:
            return '';
    }
}

export function mapLandReadinessToText(type: ListingDetailResponseDtoLandReadiness): string {
    switch (type) {
        case 'WITH_UTILITIES':
            return 'So sieťami';
        case 'WITHOUT_UTILITIES':
            return 'Bez sietí';
        case 'PARTIAL':
            return 'Čiastočne so sieťami';
        default:
            return '';
    }
}

export function mapOutdoorSpaceKindToText(kind: ListingDetailOutdoorSpaceResponseDtoKind): string {
    switch (kind) {
        case 'BALCONY':
            return 'Balkón';
        case 'LOGGIA':
            return 'Lodžia';
        case 'TERRACE':
            return 'Terasa';
        case 'CELLAR':
            return 'Pivnica';
        default:
            return '';
    }
}

export function mapOutdoorSpaceKindToIcon(
    kind: ListingDetailOutdoorSpaceResponseDtoKind,
): ComponentType<{ className?: string }> {
    switch (kind) {
        case 'BALCONY':
            return BalconyIcon;
        case 'LOGGIA':
            return LoggiaIcon;
        case 'TERRACE':
            return TerraceIcon;
        case 'CELLAR':
            return CellarIcon;
        default:
            return BalconyIcon;
    }
}

export function mapParkingTypeToText(type: ListingDetailParkingSlotResponseDtoParkingType): string {
    switch (type) {
        case 'SUFFICIENT_CAPACITY':
            return 'Dostatok parkovacích miest';
        case 'GARAGE':
            return 'Garáž';
        case 'RESIDENT_CARD':
            return 'Parkovacia karta';
        case 'RAMP_PARKING':
            return 'Parkovanie v rampe';
        case 'PRIVATE_PARKING_SPACES':
            return 'Garážové státie';
        // return 'Vonkajšie parkovacie miesta';
        case 'PRIVATE_PARKING':
            //return 'Garážové státie';
            return 'Vonkajšie parkovacie miesta';
        default:
            return '';
    }
}

export function mapParkingTypeToIcon(
    type: ListingDetailParkingSlotResponseDtoParkingType,
): ComponentType<{ className?: string }> {
    switch (type) {
        case 'GARAGE':
            return GarageIcon;
        case 'RAMP_PARKING':
            return GarageIcon;
        case 'PRIVATE_PARKING':
            return CarportIcon;
        case 'PRIVATE_PARKING_SPACES':
        case 'SUFFICIENT_CAPACITY':
        case 'RESIDENT_CARD':
        default:
            return OutdoorParkingIcon;
    }
}

// --- Formatting helpers ---

function formatArea(m2: number): string {
    return `${m2} m²`;
}

function formatRoomCount(count: number): string {
    return Number.isInteger(count) ? `${count}` : `${count}`.replace('.', ',');
}

function formatFloorNumber(floor: number): string {
    if (floor < 0) return 'Suterén';
    if (floor === 0) return 'Prízemie';
    return `${floor}`;
}

// MAIN INFO

export function getListingMainInfo(listing: Listing): ListingMainInfoItem[] {
    const d = listing.detail;
    if (!d) return [];

    const items: (ListingMainInfoItem | null)[] = [];

    if (listing.detail?.energyPerformanceClass) {
        // items.push({
        //     icon: EnergyCertIcon,
        //     label: 'Energetický certifikát',
        //     value: listing.detail.ListingEnergyPerformanceClass,
        // });
    }

    switch (listing.propertyCategory) {
        case ListingPropertyCategory.APARTMENT:
            items.push(
                d.apartmentConstructionType != null
                    ? {
                          icon: HouseIcon,
                          label: 'Typ stavby',
                          value: mapApartmentConstructionTypeToText(d.apartmentConstructionType),
                      }
                    : null,
                d.propertyConditionType != null
                    ? {
                          icon: ShinesIcon,
                          label: 'Stav',
                          value: mapPropertyConditionTypeToText(d.propertyConditionType),
                      }
                    : null,
                d.usableArea != null
                    ? { icon: AreaIcon, label: 'Úžitková plocha', value: formatArea(d.usableArea) }
                    : null,

                d.energyPerformanceClass != null
                    ? {
                          icon: EnergyCertIcon,
                          label: 'Energetický certifikát',
                          value: d.energyPerformanceClass,
                      }
                    : null,
                d.furnishingType != null
                    ? {
                          icon: FurnishingsIcon,
                          label: 'Zariadenosť',
                          value: mapFurnishingTypeToText(d.furnishingType),
                      }
                    : null,
                d.roomCount != null
                    ? {
                          icon: RoomCountIcon,
                          label: 'Počet izieb',
                          value: formatRoomCount(d.roomCount),
                      }
                    : null,
                d.floorNumber != null
                    ? {
                          icon: FloorCountIcon,
                          label: 'Poschodie',
                          value: formatFloorNumber(d.floorNumber),
                      }
                    : null,
                d.totalFloors != null
                    ? {
                          icon: FloorCountIcon,
                          label: 'Počet podlaží budovy',
                          value: `${d.totalFloors}`,
                      }
                    : null,
                d.monthlyUtilities != null
                    ? {
                          icon: MonthlyExpensesIcon,
                          label: 'Mesačné náklady',
                          value: `${d.monthlyUtilities} €/mes.`,
                      }
                    : null,
            );
            break;

        case ListingPropertyCategory.HOUSE:
            items.push(
                d.usableArea != null
                    ? { icon: AreaIcon, label: 'Úžitková plocha', value: formatArea(d.usableArea) }
                    : null,
                d.landArea != null
                    ? { icon: AreaIcon, label: 'Plocha pozemku', value: formatArea(d.landArea) }
                    : null,
                d.builtUpArea != null
                    ? {
                          icon: AreaIcon,
                          label: 'Zastavaná plocha',
                          value: formatArea(d.builtUpArea),
                      }
                    : null,
                d.buildingAge != null
                    ? { icon: CalendarIcon, label: 'Rok výstavby', value: d.buildingAge }
                    : null,
                d.propertyConditionType != null
                    ? {
                          icon: ShinesIcon,
                          label: 'Stav',
                          value: mapPropertyConditionTypeToText(d.propertyConditionType),
                      }
                    : null,
                d.houseConstructionType != null
                    ? {
                          icon: HouseIcon,
                          label: 'Typ stavby',
                          value: mapHouseConstructionTypeToText(d.houseConstructionType),
                      }
                    : null,
                d.energyPerformanceClass != null
                    ? {
                          icon: EnergyCertIcon,
                          label: 'Energetický certifikát',
                          value: d.energyPerformanceClass,
                      }
                    : null,
            );
            break;

        case ListingPropertyCategory.LAND:
            items.push(
                d.landArea != null
                    ? { icon: AreaIcon, label: 'Plocha pozemku', value: formatArea(d.landArea) }
                    : null,
                d.builtUpArea != null
                    ? {
                          icon: AreaIcon,
                          label: 'Zastavaná plocha',
                          value: formatArea(d.builtUpArea),
                      }
                    : null,
                d.landReadiness != null
                    ? {
                          icon: HouseIcon,
                          label: 'Pripravenosť',
                          value: mapLandReadinessToText(d.landReadiness),
                      }
                    : null,
            );
            break;

        case ListingPropertyCategory.RECREATIONAL:
            items.push(
                d.usableArea != null
                    ? { icon: AreaIcon, label: 'Úžitková plocha', value: formatArea(d.usableArea) }
                    : null,
                d.landArea != null
                    ? { icon: AreaIcon, label: 'Plocha pozemku', value: formatArea(d.landArea) }
                    : null,
                d.buildingAge != null
                    ? { icon: CalendarIcon, label: 'Rok výstavby', value: d.buildingAge }
                    : null,
                d.propertyConditionType != null
                    ? {
                          icon: ShinesIcon,
                          label: 'Stav',
                          value: mapPropertyConditionTypeToText(d.propertyConditionType),
                      }
                    : null,
                d.energyPerformanceClass != null
                    ? {
                          icon: EnergyCertIcon,
                          label: 'Energetický certifikát',
                          value: d.energyPerformanceClass,
                      }
                    : null,
                d.furnishingType != null
                    ? {
                          icon: ShinesIcon,
                          label: 'Zariadenie',
                          value: mapFurnishingTypeToText(d.furnishingType),
                      }
                    : null,
            );
            break;

        case ListingPropertyCategory.OTHER:
        default:
            items.push(
                d.usableArea != null
                    ? { icon: AreaIcon, label: 'Úžitková plocha', value: formatArea(d.usableArea) }
                    : null,
                d.builtUpArea != null
                    ? {
                          icon: AreaIcon,
                          label: 'Zastavaná plocha',
                          value: formatArea(d.builtUpArea),
                      }
                    : null,
                d.landArea != null
                    ? { icon: AreaIcon, label: 'Plocha pozemku', value: formatArea(d.landArea) }
                    : null,
                d.buildingAge != null
                    ? { icon: CalendarIcon, label: 'Rok výstavby', value: d.buildingAge }
                    : null,
                d.propertyConditionType != null
                    ? {
                          icon: ShinesIcon,
                          label: 'Stav',
                          value: mapPropertyConditionTypeToText(d.propertyConditionType),
                      }
                    : null,
                d.energyPerformanceClass != null
                    ? {
                          icon: EnergyCertIcon,
                          label: 'Energetický certifikát',
                          value: d.energyPerformanceClass,
                      }
                    : null,
            );
            break;
    }

    return items.filter((item): item is ListingMainInfoItem => item !== null);
}

export function getListingOutdoorSpaceItems(listing: Listing): ListingDetailedInfoItem[] {
    const spaces = listing.detail?.outdoorSpaces ?? [];
    if (!spaces.length) return [];

    const grouped = new Map<
        string,
        {
            kind: OutdoorSpaceKind;
            count: number;
            totalArea: number | null;
            sortOrder: number;
        }
    >();

    for (const space of spaces) {
        const area = space.areaM2 != null ? Number(space.areaM2) : null;
        const current = grouped.get(space.kind);

        if (!current) {
            grouped.set(space.kind, {
                kind: space.kind,
                count: 1,
                totalArea: area,
                sortOrder: space.sortOrder ?? 0,
            });
            continue;
        }

        current.count += 1;
        current.sortOrder = Math.min(current.sortOrder, space.sortOrder ?? 0);

        if (area != null) {
            current.totalArea = (current.totalArea ?? 0) + area;
        }
    }

    return [...grouped.values()]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((group) => ({
            icon: mapOutdoorSpaceKindToIcon(group.kind),
            label: mapOutdoorSpaceKindToText(group.kind),
            value: formatOutdoorSpaceValue(group),
        }));
}

function formatOutdoorSpaceValue(group: { count: number; totalArea: number | null }): string {
    if (group.totalArea != null) {
        return group.count > 1
            ? `${group.count}× / spolu ${formatArea(group.totalArea)}`
            : formatArea(group.totalArea);
    }

    return group.count > 1 ? `${group.count}×` : 'Áno';
}

export function getListingParkingItems(listing: Listing): ListingDetailedInfoItem[] {
    const detail = listing.detail;
    if (!detail) return [];

    const items: ListingDetailedInfoItem[] = [];
    const slots = detail.parkingSlots ?? [];

    for (const slot of slots.slice().sort((a, b) => a.sortOrder - b.sortOrder)) {
        const valueParts: string[] = [];

        if (slot.areaM2 != null) {
            valueParts.push(formatArea(slot.areaM2));
        }

        // if (slot.priceAmount != null) {
        //     valueParts.push(`${formatPrice(slot.priceAmount)} €`);
        // }

        items.push({
            icon: mapParkingTypeToIcon(slot.parkingType),
            label: mapParkingTypeToText(slot.parkingType),
            value: valueParts.length ? valueParts.join(' / ') : 'Áno',
            price: slot.priceAmount ?? undefined,
            area: slot.areaM2 ?? undefined,
        });
    }

    if (detail.publicParkingPlaceCount != null) {
        items.push({
            icon: OutdoorParkingIcon,
            label: 'Verejné parkovanie',
            value: `${detail.publicParkingPlaceCount} miest`,
        });
    }

    return items;
}

// DETAILED INFO

/*
export function getListingDetailedInfo(listing: Listing): DetailedInfoItem[] {
    const d = listing.detail;
    if (!d) return [];

    const items: (DetailedInfoItem | null)[] = [];

    switch (listing.propertyCategory) {
        case ListingPropertyCategory.APARTMENT:
            /*    
        items.push(
                d.roomCount != null
                    ? { label: 'Počet izieb', value: formatRoomCount(d.roomCount) }
                    : null,
                d.totalFloors != null
                    ? { label: 'Počet podlaží budovy', value: `${d.totalFloors}` }
                    : null,
                d.monthlyUtilities != null
                    ? { label: 'Mesačné náklady', value: `${d.monthlyUtilities} €/mes.` }
                    : null,
            );
            */
// items.push(...getListingOutdoorSpaceItems(listing));
// items.push(...getListingParkingItems(listing));
/* break;

        case ListingPropertyCategory.HOUSE:
            items.push(
                d.roomCount != null
                    ? { label: 'Počet izieb', value: formatRoomCount(d.roomCount) }
                    : null,
                d.floorNumber != null
                    ? { label: 'Poschodie', value: formatFloorNumber(d.floorNumber) }
                    : null,
                d.totalFloors != null
                    ? { label: 'Počet podlaží', value: `${d.totalFloors}` }
                    : null,
                d.furnishingType != null
                    ? { label: 'Zariadenie', value: mapFurnishingTypeToText(d.furnishingType) }
                    : null,
                d.monthlyUtilities != null
                    ? { label: 'Mesačné náklady', value: `${d.monthlyUtilities} €/mes.` }
                    : null,
            );
            // items.push(...getListingOutdoorSpaceItems(listing));
            // items.push(...getListingParkingItems(listing));
            break;

        case ListingPropertyCategory.LAND:
            items.push(
                d.utilitiesAvailableText != null
                    ? { label: 'Dostupné siete', value: d.utilitiesAvailableText }
                    : null,
            );
            // items.push(...getListingParkingItems(listing));
            break;

        case ListingPropertyCategory.RECREATIONAL:
            items.push(
                d.roomCount != null
                    ? { label: 'Počet izieb', value: formatRoomCount(d.roomCount) }
                    : null,
                d.builtUpArea != null
                    ? { label: 'Zastavaná plocha', value: formatArea(d.builtUpArea) }
                    : null,
                d.monthlyUtilities != null
                    ? { label: 'Mesačné náklady', value: `${d.monthlyUtilities} €/mes.` }
                    : null,
            );
            // items.push(...getListingOutdoorSpaceItems(listing));
            // items.push(...getListingParkingItems(listing));
            break;

        case ListingPropertyCategory.OTHER:
        default:
            items.push(
                d.roomCount != null
                    ? { label: 'Počet izieb / miestností', value: formatRoomCount(d.roomCount) }
                    : null,
                d.floorNumber != null
                    ? { label: 'Poschodie', value: formatFloorNumber(d.floorNumber) }
                    : null,
                d.totalFloors != null
                    ? { label: 'Počet podlaží', value: `${d.totalFloors}` }
                    : null,
                d.furnishingType != null
                    ? { label: 'Zariadenie', value: mapFurnishingTypeToText(d.furnishingType) }
                    : null,
                d.monthlyUtilities != null
                    ? { label: 'Mesačné náklady', value: `${d.monthlyUtilities} €/mes.` }
                    : null,
            );
            // items.push(...getListingOutdoorSpaceItems(listing));
            // items.push(...getListingParkingItems(listing));
            break;
    }

    return items.filter((item): item is DetailedInfoItem => item !== null);
}
*/
